import React, { useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BedDouble, Bath, Pencil, Trash2, MapPin, Tag, Archive, RotateCcw } from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { useChainStore, openEditProperty, openCreateDeal } from "../data/chainStore";
import { formatGBP } from "../lib/feeFormat";
import { toast } from "sonner";

/* iter-alignment · P0 · Property Edit → Detail View SSOT Refresh
 * (2026-02-19) — canonical currency + free-text presenter for
 * price / service charge / ground rent so a raw "2000000" saved
 * from the drawer renders as "£2,000,000" on the detail view.
 * Falls through to any pre-formatted "£1,250,000" string
 * untouched. */
const _presentMoney = (raw) => {
  if (raw === null || raw === undefined) return "—";
  const s = String(raw).trim();
  if (!s) return "—";
  const digits = s.replace(/[^0-9]/g, "");
  const nonDigitFragment = s.replace(/[0-9,\s£]/g, "").trim();
  if (!digits) return s;
  // If the string carries free-text (e.g. "£950 pcm"), preserve it verbatim.
  if (nonDigitFragment) return s;
  return formatGBP(Number(digits));
};

/* Formats "150" → "150 years" for lease length remaining.
 * Passes through any pre-formatted "125 years" untouched. */
const _presentYears = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "—";
  const s = String(raw).trim();
  if (/years?/i.test(s)) return s;
  const n = Number(String(s).replace(/[^0-9]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${n} year${n === 1 ? "" : "s"}`;
};

const STATUS_TONES = {
  Draft:          "bg-slate-100 text-slate-700 border-slate-200",
  Listed:         "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Under Offer":  "bg-amber-50 text-amber-700 border-amber-200",
  "Sales Agreed": "bg-blue-50 text-blue-700 border-blue-200",
  Exchanged:      "bg-indigo-50 text-indigo-700 border-indigo-200",
  Completed:      "bg-emerald-100 text-emerald-800 border-emerald-300",
  Withdrawn:      "bg-rose-50 text-rose-700 border-rose-200",
};

export default function PropertyProfilePage() {
  const { propertyId } = useParams();
  const nav = useNavigate();
  const {
    findProperty, findContact, dealsForProperty, deleteProperty, stages,
    isPropertyArchived, archiveProperty, restoreProperty,
  } = useChainStore();
  const property = findProperty(propertyId);
  const stageLabel = (sid) => stages.find((s) => s.id === sid)?.label || sid;
  // iter-290 — Archive / restore from the property detail header.
  // Pilot-side client-only — preserves deal rooms, documents,
  // activity, participants, revenue and forecasting history.
  const archived = isPropertyArchived?.(propertyId);
  const onArchive = useCallback(() => {
    if (!property) return;
    const ok = archiveProperty?.(property.id);
    if (!ok) return;
    toast.success("Property archived", {
      description: "Hidden from active views · history preserved",
      action: { label: "Undo", onClick: () => restoreProperty?.(property.id) },
      duration: 6000,
    });
  }, [property, archiveProperty, restoreProperty]);
  const onRestore = useCallback(() => {
    if (!property) return;
    const ok = restoreProperty?.(property.id);
    if (!ok) return;
    toast.success("Property restored", { duration: 4000 });
  }, [property, restoreProperty]);

  if (!property) {
    return (
      <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
        <Sidebar />
        <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen items-center justify-center text-center p-10" data-testid="property-not-found">
          <p className="cf-eyebrow">Not found</p>
          <h1 className="cf-h-page mt-2">Property not found</h1>
          <Link to="/properties" className="btn btn-primary mt-6"><ArrowLeft size={13} /> Back to Properties</Link>
        </main>
      </div>
    );
  }

  const seller = findContact(property.sellerId);
  const agent = findContact(property.agentId);
  const linkedDeals = dealsForProperty(property.id);

  /* iter-alignment · P0 · Canonical Price For Property (2026-02-28)
   * ---------------------------------------------------------------
   * Once a property has an active (non-terminal) deal, the deal's
   * agreed price is the number to display on the profile hero —
   * the asking price only leads when no offer has been agreed. */
  const _TERMINAL_DEAL_STAGES = ["completed", "fallen_through", "banked"];
  const _activeDeal = linkedDeals.find(
    (d) => !_TERMINAL_DEAL_STAGES.includes(String(d?.stage || "").toLowerCase())
  ) || null;
  const displayPriceRaw = _activeDeal?.agreedPrice
    ?? _activeDeal?.agreed_price
    ?? property.price;

  const onDelete = async () => {
    if (!window.confirm(`Delete ${property.address.split(",")[0]}? This cannot be undone.`)) return;
    try {
      await deleteProperty(property.id);
      toast.success("Property deleted");
      nav("/properties");
    } catch (err) {
      toast.error("Could not delete property", { description: err?.response?.data?.detail || err?.message || "Network error" });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen" data-testid="property-profile-main">
        <div className="px-4 md:px-10 py-6 md:py-9 space-y-7 max-w-[1200px]">
          <div className="pl-12 md:pl-0">
            <Link to="/properties" className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-600 hover:text-slate-900" data-testid="property-back">
              <ArrowLeft size={13} /> Properties
            </Link>
          </div>

          {/* Hero */}
          <div className="rounded-3xl overflow-hidden bg-white border border-slate-200" data-testid="property-profile-header">
            <div className="aspect-[16/6] bg-slate-100 relative">
              {property.thumb ? <img src={property.thumb} alt={property.address} className="w-full h-full object-cover" /> : null}
              <div className="absolute top-4 left-4 flex flex-wrap gap-1.5">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] border ${STATUS_TONES[property.status] || STATUS_TONES.Draft}`} data-testid="property-profile-status">
                  {property.status}
                </span>
                {archived && (
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] border bg-slate-900/85 text-white border-slate-900/30"
                    data-testid="property-profile-archived-pill"
                  >
                    <Archive size={11} /> Archived
                  </span>
                )}
              </div>
            </div>
            <div className="p-6 md:p-7 flex items-start gap-5 flex-wrap">
              <div className="flex-1 min-w-0">
                <h1 className="cf-h-page" data-testid="property-profile-address">{property.address?.split(",")[0]}</h1>
                <p className="text-[13px] text-slate-500 inline-flex items-center gap-1.5 mt-2"><MapPin size={12} /> {property.address}</p>
                <div className="flex items-center gap-4 mt-4 text-[12.5px] text-slate-700 flex-wrap">
                  <span className="inline-flex items-center gap-1.5"><BedDouble size={13} /> {property.beds} beds</span>
                  <span className="inline-flex items-center gap-1.5"><Bath size={13} /> {property.baths} baths</span>
                  <span className="text-slate-400">·</span>
                  <span>{property.type}</span>
                  <span className="text-slate-400">·</span>
                  <span className="capitalize" data-testid="property-profile-tenure">{property.tenure}</span>
                  <span className="ml-auto font-display text-[24px] font-bold cf-num text-indigo-700" data-testid="property-profile-price">{_presentMoney(displayPriceRaw)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEditProperty(property)} className="btn btn-secondary" data-testid="property-edit-btn"><Pencil size={13} /> Edit</button>
                {archived ? (
                  <button
                    onClick={onRestore}
                    className="btn btn-secondary text-emerald-700 hover:text-emerald-800"
                    data-testid="property-profile-restore-btn"
                  >
                    <RotateCcw size={13} /> Restore property
                  </button>
                ) : (
                  <button
                    onClick={onArchive}
                    className="btn btn-ghost text-slate-600 hover:text-slate-900"
                    data-testid="property-profile-archive-btn"
                  >
                    <Archive size={13} /> Archive property
                  </button>
                )}
                <button onClick={onDelete} className="btn btn-ghost text-rose-600 hover:text-rose-700" data-testid="property-delete-btn"><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          </div>

          {/* Key facts grid · iter-alignment P0 (2026-02-19) —
              Property SSOT surface now covers tenure, lease
              years remaining, ground rent, service charge,
              parking + spaces so every edited field is visible
              on reload. */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="property-key-facts">
            {[
              { k: "Postcode",          v: property.postcode || "—" },
              { k: "Tenure",            v: property.tenure   || "—" },
              { k: "Lease remaining",   v: property.tenure && String(property.tenure).toLowerCase().includes("leas") ? _presentYears(property.leaseYears) : "—" },
              { k: "EPC rating",        v: property.epc      || "—" },
              { k: "Service charge",    v: _presentMoney(property.svc) },
              { k: "Ground rent",       v: _presentMoney(property.rent) },
              { k: "Parking",           v: property.parking  || "—" },
              { k: "Parking spaces",    v: property.parkingSpaces != null && property.parkingSpaces !== "" ? String(property.parkingSpaces) : "—" },
            ].map((x) => (
              <div key={x.k} className="rounded-xl bg-white border border-slate-200 px-4 py-3" data-testid={`property-fact-${x.k.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{x.k}</p>
                <p className="text-[13.5px] font-bold text-slate-900 cf-num mt-0.5">{x.v}</p>
              </div>
            ))}
          </section>

          {/* People */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="property-people">
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="cf-eyebrow mb-1">Vendor / Seller</p>
              {seller ? (
                <Link to={`/contacts/${seller.id}`} className="block hover:bg-indigo-50 -m-2 p-2 rounded-xl transition-colors">
                  <p className="text-[14px] font-bold text-slate-900">{seller.name}</p>
                  <p className="text-[11.5px] text-slate-500">{seller.email} · {seller.phone}</p>
                </Link>
              ) : <p className="text-[12.5px] text-slate-500">Not assigned.</p>}
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <p className="cf-eyebrow mb-1">Lead agent</p>
              {agent ? (
                <Link to={`/contacts/${agent.id}`} className="block hover:bg-indigo-50 -m-2 p-2 rounded-xl transition-colors">
                  <p className="text-[14px] font-bold text-slate-900">{agent.name}</p>
                  <p className="text-[11.5px] text-slate-500">{agent.company} · {agent.email}</p>
                </Link>
              ) : <p className="text-[12.5px] text-slate-500">Not assigned.</p>}
            </div>
          </section>

          {/* Linked deals */}
          <section className="rounded-3xl bg-white border border-slate-200 p-5 md:p-6" data-testid="property-linked-deals">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="cf-eyebrow cf-eyebrow-emerald">Linked deals</p>
                <h2 className="cf-h-section text-[16px]">{linkedDeals.length} {linkedDeals.length === 1 ? "deal" : "deals"}</h2>
              </div>
              <button onClick={() => openCreateDeal({ propertyId: property.id, sellerId: property.sellerId })} className="btn btn-primary !h-9 !text-[12px]" data-testid="property-create-deal-btn">+ Create deal</button>
            </div>
            {linkedDeals.length === 0 ? (
              <p className="text-[12.5px] text-slate-500 py-6 text-center">No deals on this property yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {linkedDeals.map((d) => (
                  <li key={d.id} className="py-3 flex items-center gap-3">
                    <Tag size={13} className="text-indigo-500" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-bold text-slate-900 truncate">{d.id}</p>
                      <p className="text-[11px] text-slate-500 truncate">{stageLabel(d.stage)} · {d.status} · <span className="cf-num text-indigo-700">{d.agreedPrice}</span></p>
                    </div>
                    <Link to={`/deal-rooms/${d.id}`} className="btn btn-secondary !h-8 !text-[11.5px]" data-testid={`property-deal-${d.id}`}>Open</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Notes */}
          <section className="rounded-3xl bg-white border border-slate-200 p-5 md:p-6" data-testid="property-notes-section">
            <p className="cf-eyebrow cf-eyebrow-emerald mb-1">Notes</p>
            <h2 className="cf-h-section text-[16px] mb-3">Internal context</h2>
            <p className="text-[13px] text-slate-700 whitespace-pre-line">{property.notes || <span className="text-slate-400">No notes recorded.</span>}</p>
          </section>
        </div>
      </main>
    </div>
  );
}
